import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';
import api from '../services/api';

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};

/**
 * Renders an inline voice-note player with play/pause + waveform-like progress bar.
 * Fetches audio via authenticated axios (cookies) and creates an object URL for playback.
 */
const VoiceMessagePlayer = ({ src, durationSeconds = 0, isMe = false, messageId }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const ensureLoaded = async () => {
    if (blobUrl) return blobUrl;
    setLoading(true);
    try {
      // Strip absolute URL prefix if present so axios baseURL is used
      const relative = src.includes('/api/message-voice/')
        ? src.substring(src.indexOf('/api/message-voice/') + '/api'.length)
        : src;
      const res = await api.get(relative, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setBlobUrl(url);
      return url;
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = async () => {
    let url = blobUrl;
    if (!url) url = await ensureLoaded();
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.src = url;
      try { await audioRef.current.play(); } catch { /* user gesture may be required, ignore */ }
    }
  };

  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !a.duration) { setProgress(0); return; }
    setProgress((a.currentTime / a.duration) * 100);
  };

  const barColor = isMe ? 'bg-white/40' : 'bg-gray-300';
  const barFill = isMe ? 'bg-white' : 'bg-[#009E60]';
  const textColor = isMe ? 'text-white' : 'text-gray-800';

  return (
    <div className={`flex items-center gap-2 min-w-[180px] ${textColor}`} data-testid={`voice-message-${messageId}`}>
      <button
        onClick={togglePlay}
        className={`w-9 h-9 rounded-full flex items-center justify-center ${isMe ? 'bg-white/20 hover:bg-white/30' : 'bg-[#009E60]/10 hover:bg-[#009E60]/20'}`}
        aria-label={playing ? 'Pause voice' : 'Play voice'}
        data-testid={`voice-play-${messageId}`}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <div className={`flex-1 h-1.5 rounded-full ${barColor} overflow-hidden`}>
        <div className={`h-full ${barFill} transition-[width] duration-150`} style={{ width: `${progress}%` }} />
      </div>
      <span className="text-xs font-medium tabular-nums" data-testid={`voice-duration-${messageId}`}>{fmt(durationSeconds || 0)}</span>
      <audio
        ref={audioRef}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onTimeUpdate={onTimeUpdate}
        preload="none"
      />
    </div>
  );
};

export default VoiceMessagePlayer;
